import React, { Suspense, lazy } from 'react';
import {Routes, Route, createBrowserRouter, RouterProvider} from 'react-router-dom';

import { setLocale } from 'yup';

import ErrorBoundary from "../components/ErrorBoundary";
import DelayedFallback from "../components/common/DelayedFallback";
import Loader from "../components/common/Loader";
import { resolveFallback } from "../helpers/resolveRouteFallback";

import { ProvideAuth} from "../services/use-auth";
import { ProvideConfig } from "../services/use-config";
import { ToastProvider } from "../components/common/Toast";
import { PrivateRoutesConfig } from '../config/PrivateRoutesConfig';

const NotFoundPage = lazy(() => import("./NotFoundPage"));
const LoginPage = lazy(() => import("./LoginPage"));
const ForgotPasswordPage = lazy(() => import("./ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./ResetPasswordPage"));
const InviteAcceptPage = lazy(() => import("./InviteAcceptPage"));
const ChangePasswordPage = lazy(() => import("./ChangePasswordPage"));
const MainPage = lazy(() => import("./MainPage"));
const PrivateBrowserRoute = lazy(() => import("../components/PrivateBrowserRoute"));

//Defining a custom locale dictionary for Form Validation
setLocale({
	   mixed: {
		  default: 'form.validation.mixed.default',
		  required: 'form.validation.mixed.required',
	   },
	   string: {
		     email: 'form.validation.string.email',
		   },
	   object: {
			mixed: {
		  		default: 'form.validation.mixed.default',
		  		required: 'form.validation.mixed.required',
	   		},
	   }
	 });

const AppRoutes = props => {

	let privateRoutes = PrivateRoutesConfig.map((route, index) => {
		    const {
		    	path,
		    	component: Component,
		    	title,
		    	roles,
		    	permissions,
				hierarchy,
				fallback,
		    	...rest
		    } = route;

		    // Suspense per-route com skeleton correto. delay 400ms bate com o
		    // useShowAfter interno do PagedList (chunk rápido = nada visível;
		    // chunk lento = mesmo skeleton do início ao fim, sem flip).
		    return (
				<Route key={index} path={path} element={
					<Suspense fallback={<DelayedFallback delay={400}>{resolveFallback(fallback)}</DelayedFallback>}>
						<Component />
					</Suspense>
				} />
		    )
	})
	
	return (
		<Routes>
			<Route exact path="/login" element={<LoginPage/>} />
	     	<Route exact path="/forgot-password" element={<ForgotPasswordPage />} />
	     	<Route exact path="/reset-password/:token" element={<ResetPasswordPage />}  />
	     	<Route exact path="/invite/:token" element={<InviteAcceptPage />}  />
			<Route exact path="/change-password" element={<ChangePasswordPage />} />

	     	<Route path="/" element={<PrivateBrowserRoute/>}>
				<Route element={<MainPage/>}>
					{privateRoutes}
					{/* 404 dentro do shell: sidebar + topbar preservados; usuário não é
					    "expulso" do portal, só errou o caminho. PrivateBrowserRoute manda
					    unauthenticated pra /login antes de chegar aqui. */}
					<Route path="*" element={<NotFoundPage />} />
				</Route>
			</Route>
	     </Routes>
	);
}

const router = createBrowserRouter([
  	{ path: "*", Component: AppRoutes },
]);

const Root = props => {

	return (
		<ProvideConfig>
		  <ProvideAuth>
			<ToastProvider>
	     		<RouterProvider router={router} />
			</ToastProvider>
		 </ProvideAuth>
	   </ProvideConfig>
	  );
}

// here app catches the suspense from page in case translations are not yet loaded
export default function App() {
  return (
    <Suspense fallback={<DelayedFallback><Loader /></DelayedFallback>}>
    	<ErrorBoundary>
    		<Root />
    	</ErrorBoundary>
    </Suspense>
  );
}
